/**
 * InlineAddBar — Notion-style "+" handle that appears between sections.
 *
 * Hover the area to reveal a thin line + "+" button. Click → a small popover
 * with options (Fields section, Table2section). Designed to feel like a doc
 * editor, not a form builder.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, LayoutList, Table2, Sparkles, Wand2,
} from 'lucide-react';

export default function InlineAddBar({
  onAddFields,
  onAddTable,
  onOpenTemplates,
  onAskAI,
  variant = 'between', // 'between' | 'end'
  label,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handle = (fn) => () => { fn?.(); setOpen(false); };

  return (
    <div
      ref={containerRef}
      className={`fmx-inline-add fmx-inline-add--${variant} ${open ? 'is-open' : ''}`}
    >
      <button
        type="button"
        className="fmx-inline-add__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={label || t('fieldsManager.inlineAdd.addASection')}
        title={label || t('fieldsManager.inlineAdd.addASection')}
      >
        <span className="fmx-inline-add__line" aria-hidden="true" />
        <span className="fmx-inline-add__btn">
          <Plus />
          {variant === 'end' && <span>{label || t('fieldsManager.inlineAdd.addSection')}</span>}
        </span>
        <span className="fmx-inline-add__line" aria-hidden="true" />
      </button>

      {open && (
        <div className="fmx-inline-add__menu" role="menu">
          <button type="button" role="menuitem" onClick={handle(onAddFields)}>
            <span className="fmx-inline-add__menu-icon fmx-inline-add__menu-icon--fields">
              <LayoutList />
            </span>
            <span>
              <strong>{t('fieldsManager.sidebar.fieldsSection')}</strong>
              <em>{t('fieldsManager.sidebar.fieldsHint')}</em>
            </span>
          </button>
          <button type="button" role="menuitem" onClick={handle(onAddTable)}>
            <span className="fmx-inline-add__menu-icon fmx-inline-add__menu-icon--table">
              <Table2/>
            </span>
            <span>
              <strong>{t('fieldsManager.sidebar.tableSection')}</strong>
              <em>{t('fieldsManager.inlineAdd.tableSectionHint')}</em>
            </span>
          </button>
          <div className="fmx-inline-add__menu-sep" />
          <button type="button" role="menuitem" onClick={handle(onOpenTemplates)}>
            <span className="fmx-inline-add__menu-icon fmx-inline-add__menu-icon--tpl">
              <Wand2 />
            </span>
            <span>
              <strong>{t('fieldsManager.inlineAdd.fromTemplate')}</strong>
              <em>{t('fieldsManager.inlineAdd.fromTemplateHint')}</em>
            </span>
          </button>
          <button type="button" role="menuitem" onClick={handle(onAskAI)}>
            <span className="fmx-inline-add__menu-icon fmx-inline-add__menu-icon--ai">
              <Sparkles />
            </span>
            <span>
              <strong>{t('fieldsManager.sidebar.askAI')}</strong>
              <em>{t('fieldsManager.inlineAdd.askAIHint')}</em>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
